import { useQuery as useReactQuery } from "@tanstack/react-query";
import { useAction, usePaginatedQuery, useQuery } from "convex/react";
import { DownloadIcon, FileIcon, Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription } from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

interface MatterDocumentsProps {
	connectionId: Id<"oauthConnections">;
	clioMatterId: number;
}

function formatSize(n: number | undefined): string {
	if (n === undefined) return "—";
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
	return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function MatterDocuments({
	connectionId,
	clioMatterId,
}: MatterDocumentsProps) {
	const pagination = usePaginatedQuery(
		api.clio.queries.listDocuments,
		{ connectionId, clioMatterId },
		{ initialNumItems: 25 },
	);
	// usePaginatedQuery lacks needsRefresh; read via single-page useQuery with same args.
	const firstPage = useQuery(api.clio.queries.listDocuments, {
		connectionId,
		clioMatterId,
		paginationOpts: { numItems: 1, cursor: null },
	});
	const needsRefresh = firstPage?.needsRefresh ?? false;

	const getDownloadUrl = useAction(api.clio.actions.getDocumentDownloadUrl);
	const fetchMatterDocuments = useAction(api.clio.actions.fetchMatterDocuments);

	// Auto-refreshes without useEffect: tanstack-query `enabled: needsRefresh`
	// runs at most once per (connection, matter) key, re-firing on a new
	// matter or once needsRefresh flips false after the fetch completes.
	const refresh = useReactQuery({
		queryKey: ["clio-fetch-matter-documents", connectionId, clioMatterId],
		queryFn: () => fetchMatterDocuments({ connectionId, clioMatterId }),
		enabled: needsRefresh,
		retry: false,
		staleTime: Number.POSITIVE_INFINITY,
	});

	const [downloadingId, setDownloadingId] = useState<number | null>(null);
	const [downloadError, setDownloadError] = useState<string | null>(null);

	const onDownload = async (clioDocumentId: number) => {
		setDownloadingId(clioDocumentId);
		setDownloadError(null);
		try {
			const { url } = await getDownloadUrl({
				connectionId,
				clioDocumentId,
			});
			window.open(url, "_blank", "noopener,noreferrer");
		} catch (err) {
			setDownloadError(err instanceof Error ? err.message : "Download failed");
		} finally {
			setDownloadingId(null);
		}
	};

	const refreshError =
		refresh.error instanceof Error
			? refresh.error.message
			: refresh.error
				? "Failed to refresh documents"
				: null;
	const bannerError = downloadError ?? refreshError;
	// isFetching (not isPending): tanstack-query holds isPending:true whenever
	// enabled:false, so only isFetching reliably signals "fetch in flight".
	const refreshInFlight = refresh.isFetching;
	const isInitialFetch =
		pagination.results.length === 0 && (refreshInFlight || needsRefresh);

	if (
		pagination.status === "LoadingFirstPage" &&
		pagination.results.length === 0
	) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>Loading documents…</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}
	if (isInitialFetch) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
					<EmptyDescription>Fetching documents from Clio…</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}
	if (pagination.results.length === 0) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>No documents on this matter.</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}

	return (
		<ScrollArea className="h-full min-h-0 flex-1">
			<div className="flex flex-col">
				{bannerError && (
					<div className="border-b bg-destructive/10 px-4 py-2 text-xs text-destructive">
						{bannerError}
					</div>
				)}
				{refreshInFlight && pagination.results.length > 0 && (
					<div className="flex items-center gap-2 border-b bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
						<Loader2 className="h-3 w-3 animate-spin" />
						Refreshing documents from Clio…
					</div>
				)}
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Name</TableHead>
							<TableHead>Category</TableHead>
							<TableHead>Size</TableHead>
							<TableHead>Author</TableHead>
							<TableHead>Last edit</TableHead>
							<TableHead className="w-[100px]"> </TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{pagination.results.map((doc) => (
							<TableRow key={doc._id}>
								<TableCell className="max-w-[320px]">
									<div className="flex items-center gap-2">
										<FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
										<span className="truncate font-medium">{doc.name}</span>
									</div>
								</TableCell>
								<TableCell className="text-xs text-muted-foreground">
									{doc.categoryName ?? "—"}
								</TableCell>
								<TableCell className="text-xs text-muted-foreground">
									{formatSize(doc.size)}
								</TableCell>
								<TableCell className="text-xs text-muted-foreground">
									{doc.authorName ?? "—"}
								</TableCell>
								<TableCell className="text-xs text-muted-foreground">
									{new Date(doc.clioUpdatedAt).toLocaleDateString()}
								</TableCell>
								<TableCell>
									<Button
										size="sm"
										variant="ghost"
										onClick={() => onDownload(doc.clioId)}
										disabled={downloadingId === doc.clioId}
									>
										<DownloadIcon className="h-4 w-4" />
										<span className="sr-only">Download</span>
									</Button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
				{pagination.status === "CanLoadMore" && (
					<div className="p-3">
						<Button
							variant="outline"
							size="sm"
							className="w-full"
							onClick={() => pagination.loadMore(25)}
						>
							Load more
						</Button>
					</div>
				)}
			</div>
		</ScrollArea>
	);
}
